// penEraserTip — 液タブ (= Wacom Cintiq / XP-Pen / Huion 等) のペン尻
// (= eraser tip) を消しゴム入力として扱うための pure helper.
//
// ## 背景・経緯 (Java 版踏襲、2026-05-21 実装)
//
// 旧 Java 版 (`OekakiChatJava/OekakiChatJavaApplet/`) は JTablet 経由で
// `JTabletCursor.getCursorType()` が `TYPE_PEN_ERASER (=2)` を返したとき:
//   1. 通常のペンツール状態 (= UI 上のツール選択) は触らない
//   2. その stroke の color / alpha / 太さだけを「消しゴム」相当に override
//   3. ペン先に戻したら次の stroke は元の設定で描く
// という挙動だった (`WhiteboardCanvas.java:1150-1162`).
//
// Web の PointerEvent では eraser tip を以下で報告 (W3C, Wacom / XP-Pen /
// Huion ドライバ標準準拠):
//   - `pointerType === 'pen'` かつ
//   - `button === 5` (`pointerdown` の瞬間) または
//   - `buttons & 32` (= bit5, `pointerdown` / `pointermove` 中の持続フラグ)
//
// `button` は変化時のみセットされる「いま発生したボタン」, `buttons` は
// 現在押下中の bitmask. eraser tip が「触れている = pen down」状態を表す
// なら `button === 5` (触れた瞬間) と `buttons & 32` (継続) のいずれかで
// 検出できる. pointerdown 時点で両方確認しておけば取りこぼしがない.
//
// ## 仕様 (Java 版踏襲)
//
// - **トリガ**: 上記の eraser tip 判定が true のとき.
// - **動作**: その stroke の PenConfig を `pen='eraser'` で override.
//   color / alpha / 太さ等の他フィールドは UI 上の現選択をそのまま使う
//   (= drawing-core の eraser pen 実装が必要なフィールドだけ参照).
// - **判定タイミング**: `pointerdown` 時に 1 度評価. stroke 途中で
//   ペン先 ⇄ ペン尻 が切り替わっても、その stroke は最初の判定を維持
//   (= Java 版は途中切替で release+press し直してたが、Web の typical
//   ユーザは描き始めにペンを反転させる挙動が標準で、途中切替の頻度は低い.
//   実装単純化のため pointerdown 時点で確定する).
// - **UI 表示変更**: ツールパレットハイライト変更・cursor 変更ともになし
//   (= Java 版がそうだったため、旧版ユーザの肌感覚踏襲).
// - **自動復帰**: stroke 終了で eraser tip フラグは破棄、次の pointerdown
//   で再評価. ペン先に戻したら即元の選択ペンに復帰.
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は **node env, no DOM**
// (`packages/web/vitest.config.ts`). PointerEvent を node 環境で組み立て
// にくいため、判定ロジックを pure 関数に切り出して単体テスト可能にする.
// 既存 `penOnlyMode.ts` / `spuit.ts` と同じ流儀.
//
// ## Modern Layout 対応
//
// 本実装は Classic Layout 専用. Modern Layout (Phase 11+) では UI 上の
// ハイライト変更 (= CSP / Procreate 流儀) も検討する余地があるため、
// 採用する helper を再利用するかは別途検討. TODO は
// `docs/_wip/architecture.md` に記録予定.

/**
 * PointerEvent.button が eraser tip を意味する値.
 *
 * W3C PointerEvent Level 3 / WHATWG HTML spec で `button === 5` は
 * 「pen eraser tip pointing forward」と定義されている. Wacom / XP-Pen /
 * Huion の Windows / macOS ドライバはこの値を返す.
 */
export const PEN_ERASER_BUTTON = 5;

/**
 * PointerEvent.buttons (= bitmask) で eraser tip が触れていることを表す bit.
 *
 * `buttons & 32` (= bit 5) で eraser tip が active. button === 5 と異なり、
 * 「いま値が変化した」ではなく「現在も押下中」を意味する.
 */
export const PEN_ERASER_BUTTONS_BIT = 32;

/**
 * PointerEvent から「pen eraser tip による入力か」を判定する.
 *
 * - `pointerType !== 'pen'` のときは常に false (= マウス / タッチでは
 *   eraser tip 入力は存在しない).
 * - `pointerType === 'pen'` のとき、以下のいずれかで true:
 *   - `button === 5` (= pointerdown / pointerup の瞬間に新たに発生 / 解除)
 *   - `buttons & 32 !== 0` (= 継続的に押下中)
 *
 * pointerdown 時に呼ばれる想定. pointermove 中の途中変化は判定しない
 * (Java 版踏襲: 描き始め時点の判定を stroke 内で維持).
 *
 * 引数は `PointerEvent` 直接ではなく必要 field だけ取る型にする (= test
 * 容易性、node env で `new PointerEvent()` できないため).
 */
export interface PenEraserTipPointerLike {
  readonly pointerType: string;
  readonly button: number;
  readonly buttons: number;
}

export const isPenEraserTipPointer = (e: PenEraserTipPointerLike): boolean => {
  if (e.pointerType !== 'pen') return false;
  if (e.button === PEN_ERASER_BUTTON) return true;
  if ((e.buttons & PEN_ERASER_BUTTONS_BIT) !== 0) return true;
  return false;
};
